import { useLocationSearch } from '@rocketlane/rocket-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { proxy as comlinkProxy } from 'comlink';
import { isEmpty, keyBy, unionBy, uniq } from 'lodash';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { ProjectId, UserStatus, UserType } from 'api/models';
import { ProjectAPI } from 'api/project';
import { UserAPI } from 'api/user';
import { toastService } from 'components/molecules/toast';
import { MembersAPI } from 'data/members/api';
import { invalidateSwrUser } from 'hooks/use-swr-user-hooks';
import { useTranslate } from 'hooks/use-translation';
import { getProjectMembersQueryKey, queryClient as queryClientDefault, useProjectMembersQuery } from 'queries';
import { putAll as putAllTeams } from 'stores/teams';
import { putAll as putAllUsers } from 'stores/users';
import { ActiveUsersAndTeams } from 'utils/active-users-and-teams';
import { callWorkerWithCleanup } from 'utils/call-worker-with-cleanup';
import { userOption } from 'utils/project-fields/construct-user-option';
import { oneDay, oneHour } from './constants';
import { getPeopleHierarchyQueryKey, getPeopleListQueryKey } from './customer-company';
import { useHideNonStrategicPartner } from './partner';
import { getProjectPlaceholdersQueryKey, getProjectPlaceholdersWithMetaQueryKey } from './placeholder';
export const MEMBERS_V2_QUERY_KEY = [
    'members-v2'
];
export const TEAMS_QUERY_KEY = [
    'project-teams'
];
export const MEMBERS_V2_MODIFIED_QUERY_KEY = [
    'members-v2-modified'
];
export const TEAMS_WITH_MEMBERS_QUERY_KEY = [
    'teams-with-members'
];
export const ALL_MEMBERS_QUERY_KEY = [
    'all-members'
];
export const ALL_MEMBERS_MAP_QUERY_KEY = [
    'all-members-map'
];
export const ASSIGNE_PICKER_DATA_QUERY_KEY = (searchString)=>[
        'assignee-picker-data',
        searchString
    ];
export const BaseMembersQueryOptions = {
    queryKey: ALL_MEMBERS_QUERY_KEY,
    queryFn: ()=>MembersAPI.findAll(),
    staleTime: oneDay
};
const BaseMembersV2QueryOptions = {
    queryKey: MEMBERS_V2_QUERY_KEY,
    staleTime: oneHour
};
const BaseTeamsWithMembersQueryOptions = {
    queryKey: TEAMS_WITH_MEMBERS_QUERY_KEY,
    staleTime: oneHour
};
export const activeUsersAndTeamsFromIndexedB = new ActiveUsersAndTeams();
// @TODO: This need to be moved as a custom hook and called inside a component.
// @TODO: Remove the react query call and move the new memebers api call inside the worker.
function processMembersDataInWorker(documents, onDataSeededIntoIndexedDB) {
    const worker = callWorkerWithCleanup();
    worker.workerApi.processMembersData(documents, comlinkProxy(({ users, teams })=>{
        activeUsersAndTeamsFromIndexedB.setActiveUsersAndTeams = {
            users,
            teams
        };
        onDataSeededIntoIndexedDB === null || onDataSeededIntoIndexedDB === void 0 ? void 0 : onDataSeededIntoIndexedDB();
        // clean up worker after members and teams data inserted
        worker.cleanup();
    }));
}
const transformTeams = (teams)=>{
    return teams.map((team)=>{
        var _ref;
        const { projectFinancialsBudgetIds } = team;
        // projectFinancialsBudgetIds is optional from API; fall back to team.budgetIds or []
        const budgetIds = (_ref = projectFinancialsBudgetIds !== null && projectFinancialsBudgetIds !== void 0 ? projectFinancialsBudgetIds : team.budgetIds) !== null && _ref !== void 0 ? _ref : [];
        return {
            ...team,
            budgetIds
        };
    });
};
const transformMembers = (data, isCurrentUserCustomer, updateCompanyIfNonStrategic)=>{
    var _ref, _ref1;
    const suggestionsData = [
        ...Object.values((_ref = data === null || data === void 0 ? void 0 : data.users) !== null && _ref !== void 0 ? _ref : {})
    ];
    const membersData = suggestionsData.map((user)=>({
            ...user,
            userId: user.userId
        }));
    const users = isCurrentUserCustomer() ? membersData.map((member)=>({
            ...member,
            company: updateCompanyIfNonStrategic(member.company)
        })) : membersData;
    const teams = transformTeams(Object.values((_ref1 = data === null || data === void 0 ? void 0 : data.teams) !== null && _ref1 !== void 0 ? _ref1 : {}));
    return {
        users,
        teams
    };
};
/**
 * @deprecated
 * DO NOT USE THIS HOOK, IT IS DEPRECATED AND WILL BE REMOVED IN THE FUTURE.
 */ export const useMembersV2Query = ({ queryKey, queryFn, staleTime, select, ...restOptions }, onDataSeededIntoIndexedDB)=>{
    // console.log(
    //   '🟣 useMembersV2Query 1 called from:',
    //   new Error().stack?.split('\n')[2],
    // );
    const dispatch = useDispatch();
    const search = useLocationSearch();
    const queryParams = new URLSearchParams(search);
    const isCustomerPreview = !!queryParams.get('customer-preview');
    const { isDependentEntitiesLoading, isCurrentUserCustomer, updateCompanyIfNonStrategic } = useHideNonStrategicPartner();
    return useQuery({
        ...BaseMembersV2QueryOptions,
        queryFn: ()=>MembersAPI.getMembersV2().then((data)=>{
                // These dispatch methods were moved from app-boot-data.tsx after removing the api call to /members on page load
                const { users, teams } = transformMembers(data, isCurrentUserCustomer, updateCompanyIfNonStrategic);
                dispatch(putAllUsers([]));
                dispatch(putAllTeams(teams !== null && teams !== void 0 ? teams : []));
                queryClientDefault.setQueriesData(MEMBERS_V2_QUERY_KEY, data);
                // In project customer preview we don't want to re-populate the indexDb with just members of a specific project.
                if (isCustomerPreview) {
                    onDataSeededIntoIndexedDB === null || onDataSeededIntoIndexedDB === void 0 ? void 0 : onDataSeededIntoIndexedDB();
                } else {
                    // Handle storing data to indexeddb inside the web worker
                    processMembersDataInWorker({
                        teams,
                        users
                    }, onDataSeededIntoIndexedDB);
                }
                return data;
            }),
        enabled: !isDependentEntitiesLoading,
        ...restOptions,
        refetchOnWindowFocus: false
    });
};
export const membersV2TransformedQueryFn = async (isCurrentUserCustomer, updateCompanyIfNonStrategic)=>{
    let data = queryClientDefault.getQueryData(MEMBERS_V2_QUERY_KEY);
    if (!data) {
        data = await MembersAPI.getMembersV2();
        if (!data) {
            return {};
        }
    }
    const { users, teams } = transformMembers(data, isCurrentUserCustomer, updateCompanyIfNonStrategic);
    return {
        users,
        teams
    };
};
export const teamsTransformedQueryFn = async (isCurrentUserCustomer, updateCompanyIfNonStrategic)=>{
    let data = queryClientDefault.getQueryData(TEAMS_QUERY_KEY);
    if (!data) {
        data = await MembersAPI.getTeams();
        if (!data) {
            return {};
        }
    }
    const { teams } = transformMembers({
        teams: data,
        users: {}
    }, isCurrentUserCustomer, updateCompanyIfNonStrategic);
    return teams;
};
export const useMembersV2TransformedQuery = ()=>{
    // console.log(
    //   '🟪 useMembersV2TransformedQuery called from:',
    //   new Error().stack?.split('\n')[2],
    // );
    const { isCurrentUserCustomer, updateCompanyIfNonStrategic } = useHideNonStrategicPartner();
    return useQuery(MEMBERS_V2_MODIFIED_QUERY_KEY, ()=>membersV2TransformedQueryFn(isCurrentUserCustomer, updateCompanyIfNonStrategic), {});
};
export const useTeamsTransformedQuery = ()=>{
    const { isCurrentUserCustomer, updateCompanyIfNonStrategic } = useHideNonStrategicPartner();
    return useQuery(TEAMS_QUERY_KEY, ()=>teamsTransformedQueryFn(isCurrentUserCustomer, updateCompanyIfNonStrategic), {});
};
const transformTeamsWithMembers = (data)=>{
    const transformedTeamsMap = Object.entries(data.teamsMap).reduce((acc, [teamId, team])=>{
        const budgetIds = team.projectToBudgetsMap ? Object.values(team.projectToBudgetsMap).flat() : [];
        const projectIds = team.projectToBudgetsMap ? Object.keys(team.projectToBudgetsMap).map(Number) : team.projectIds || [];
        acc[teamId] = {
            ...team,
            budgetIds,
            projectIds
        };
        return acc;
    }, {});
    return {
        teamsMap: transformedTeamsMap
    };
};
// @deprecated
export const useTeamsWithMembersQuery = (options = {})=>{
    return useQuery({
        ...BaseTeamsWithMembersQueryOptions,
        queryFn: ()=>MembersAPI.getTeamsWithMembers().then(transformTeamsWithMembers),
        ...options
    });
};
/**
 *
 * @description Use this hook to manage team placeholders in MembersV2Modified query cache.
 */ export const useManageTeamsInProject = ()=>{
    const addTeamMemberToProject = useCallback((team, projectId)=>{
        var _teamToBeModified_projectIds, _team_projectIds;
        const { users, teams } = queryClientDefault.getQueryData(MEMBERS_V2_MODIFIED_QUERY_KEY);
        const teamToBeModified = teams.find((t)=>t.teamId === team.teamId);
        if (!teamToBeModified) {
            return {
                users,
                teams
            };
        }
        const newTeams = teams.filter((t)=>t.teamId !== team.teamId).concat({
            ...teamToBeModified,
            ...team,
            projectIds: uniq(((_teamToBeModified_projectIds = teamToBeModified.projectIds) !== null && _teamToBeModified_projectIds !== void 0 ? _teamToBeModified_projectIds : []).concat([
                ...(_team_projectIds = team.projectIds) !== null && _team_projectIds !== void 0 ? _team_projectIds : [],
                projectId
            ]))
        });
        queryClientDefault.setQueryData(MEMBERS_V2_MODIFIED_QUERY_KEY, {
            users,
            teams: newTeams
        });
        return {
            users,
            teams: newTeams
        };
    }, []);
    const removeTeamMemberFromProject = useCallback((team, projectId)=>{
        var _teamToBeModified_projectIds, _team_projectIds;
        queryClientDefault.setQueryData(getProjectPlaceholdersWithMetaQueryKey(ProjectId(projectId)), (old = [])=>old.filter((t)=>t.teamId !== team.teamId));
        queryClientDefault.setQueryData(getProjectPlaceholdersQueryKey(ProjectId(projectId)), (old = [])=>old.filter((t)=>t.teamId !== team.teamId));
        const cacheData = queryClientDefault.getQueryData(MEMBERS_V2_MODIFIED_QUERY_KEY);
        if ((cacheData === null || cacheData === void 0 ? void 0 : cacheData.teams) == null) {
            var _ref, _ref1;
            return {
                users: (_ref = cacheData === null || cacheData === void 0 ? void 0 : cacheData.users) !== null && _ref !== void 0 ? _ref : [],
                teams: (_ref1 = cacheData === null || cacheData === void 0 ? void 0 : cacheData.teams) !== null && _ref1 !== void 0 ? _ref1 : []
            };
        }
        const { users, teams } = cacheData;
        const teamToBeModified = teams.find((t)=>t.teamId === team.teamId);
        if (!teamToBeModified) {
            return {
                users,
                teams
            };
        }
        const newTeams = teams.filter((t)=>t.teamId !== team.teamId).concat({
            ...teamToBeModified,
            ...team,
            projectIds: uniq(((_teamToBeModified_projectIds = teamToBeModified.projectIds) !== null && _teamToBeModified_projectIds !== void 0 ? _teamToBeModified_projectIds : []).concat([
                ...(_team_projectIds = team.projectIds) !== null && _team_projectIds !== void 0 ? _team_projectIds : []
            ])).filter((t)=>t !== projectId)
        });
        queryClientDefault.setQueryData(MEMBERS_V2_MODIFIED_QUERY_KEY, {
            users,
            teams: newTeams
        });
        return {
            users,
            teams: newTeams
        };
    }, []);
    return {
        addTeamMemberToProject,
        removeTeamMemberFromProject
    };
};
/**
 * @deprecated
 * DO NOT USE THIS HOOK, IT IS DEPRECATED AND WILL BE REMOVED IN THE FUTURE.
 */ export const useMembersQuery = ({ queryKey, queryFn, staleTime, ...restOptions } = {})=>{
    // console.log(
    //   '🟨 useMembersQuery called from:',
    //   new Error().stack?.split('\n')[2],
    // );
    return useQuery({
        ...BaseMembersQueryOptions,
        ...restOptions
    });
};
/**
 * @deprecated
 * DO NOT USE THIS HOOK, IT IS DEPRECATED AND WILL BE REMOVED IN THE FUTURE.
 */ export const useNativeMembersQuery = ({ queryKey, queryFn, staleTime, select, ...restOptions } = {})=>{
    // console.log(
    //   '🟨 useNativeMembersQuery called from:',
    //   new Error().stack?.split('\n')[2],
    // );
    return useQuery({
        ...BaseMembersQueryOptions,
        select: ({ users, members, ...rest })=>{
            const result = {
                ...rest,
                users: (users !== null && users !== void 0 ? users : []).filter(({ userType })=>userType === UserType.Native),
                members: (members !== null && members !== void 0 ? members : []).filter(({ user })=>user.userType === UserType.Native)
            };
            return result;
        },
        ...restOptions
    });
};
export const ALL_USERS_QUERY_KEY = ALL_MEMBERS_QUERY_KEY;
/**
 * @deprecated
 */ export const useUsersQuery = ()=>{
    // console.log(
    //   '🔵 useUsersQuery called from:',
    //   new Error().stack?.split('\n')[2],
    // );
    return useQuery({
        ...BaseMembersQueryOptions,
        select: (res)=>{
            return res.members.map((member)=>member.user);
        }
    });
};
/**
 * @deprecated
 * very bad hook, why even do this? why cant the backend handle this?
 */ export const useUsersMapQuery = ()=>{
    // console.log(
    //   '🟨 useUsersMapQuery called from:',
    //   new Error().stack?.split('\n')[2],
    // );
    return useQuery({
        ...BaseMembersQueryOptions,
        select: (res)=>{
            return new Map(res.members.map((member)=>[
                    member.user.userId,
                    member.user
                ]));
        }
    });
};
/**
 * @deprecated
 */ export const useNativeActiveOrInvitedUsersQuery = ()=>{
    return useQuery({
        ...BaseMembersQueryOptions,
        select: (res)=>{
            return res.members.map(({ user })=>user).filter(({ userStatus, userType })=>(userStatus === UserStatus.Active || userStatus === UserStatus.Invited) && userType === UserType.Native);
        },
        refetchOnMount: true
    });
};
export const useMembersFieldsAccessMetaQuery = ()=>{
    return useQuery({
        ...BaseMembersQueryOptions,
        select: (res)=>res.fieldsAccessMeta
    });
};
/**
 * @deprecated
 *
 * ⚠️ MISLEADING NAME: Despite the name, this returns Native + Partner users (excludes only External)
 * For truly Native-only users, use useAsyncNativeAccountUsers() from hooks/use-idb.ts
 *
 * Returns: Native users + Partner users (Active + Invited + Inactive)
 * Excludes: External users only
 *
 * Migration: Use useAsyncNativeAccountUsers() for Native-only or useAsyncAccountUsers() for Native+Partner
 */ export const useNativeUsersQuery = ()=>{
    // console.log(
    //   '🔴 useNativeUsersQuery called from:',
    //   new Error().stack?.split('\n')[2],
    // );
    return useQuery({
        ...BaseMembersQueryOptions,
        select: (res)=>{
            return res.members.map(({ user })=>user).filter(({ userType })=>userType !== UserType.External);
        }
    });
};
/**
 * ⚠️ MISLEADING NAME: Despite the name, this returns Native + Partner users (excludes only External)
 * For truly Native-only users, use useAsyncNativeAccountUsers() from hooks/use-idb.ts
 *
 * Used for: View management (view restrictions can include Partner users)
 * Returns: Native users + Partner users (excludes External, excludes API keys)
 * Includes: Active + Invited + Inactive (for historical data)
 *
 * @param filterFn - Optional additional filtering
 * @param includedUserIds - Pre-selected user IDs to ensure they're loaded (for edit mode)
 * @param enabled - Control when query runs (lazy loading)
 *
 * Migration Note: Use useAsyncNativeAccountUsers() for Native-only sprint/epic/backlog owners
 */ export const useAsyncNativeUsers = (filterFn, includedUserIds, enabled = true)=>{
    const { data: defaultUsers } = useQuery([
        'async-native-users',
        filterFn === null || filterFn === void 0 ? void 0 : filterFn.toString(),
        includedUserIds
    ], async ()=>{
        var _data_includedUsers;
        const data = await MembersAPI.fetchPaginatedMembersData({
            projectId: -1,
            startRow: 0,
            endRow: 100,
            includedUserIds
        });
        const allUsers = [
            ...data.inProjectUsers,
            ...data.notInProjectUsers,
            ...(_data_includedUsers = data.includedUsers) !== null && _data_includedUsers !== void 0 ? _data_includedUsers : []
        ];
        const filteredUsers = allUsers.filter((user)=>user.userType !== UserType.External && // Filter out API key users (UUID emails without @)
            (!user.emailId || user.emailId.includes('@')) && (filterFn ? filterFn(user) : true));
        return filteredUsers;
    }, {
        enabled
    });
    const loadOptions = useCallback(async (input)=>{
        const data = await MembersAPI.fetchPaginatedMembersData({
            projectId: -1,
            searchString: input,
            startRow: 0,
            endRow: 100
        });
        const allUsers = [
            ...data.inProjectUsers,
            ...data.notInProjectUsers
        ];
        return allUsers.filter((user)=>user.userType !== UserType.External && // Filter out API key users (UUID emails without @)
            (!user.emailId || user.emailId.includes('@')) && (filterFn ? filterFn(user) : true));
    }, [
        filterFn
    ]);
    return {
        defaultOptions: defaultUsers !== null && defaultUsers !== void 0 ? defaultUsers : [],
        loadOptions
    };
};
export const useAsyncCustomerUsers = (companyId)=>{
    const numericCompanyId = typeof companyId === 'number' ? companyId : undefined;
    const { data: defaultUsers } = useQuery([
        'async-customer-users',
        numericCompanyId
    ], async ()=>{
        const data = await MembersAPI.fetchPaginatedMembersData({
            projectId: -1,
            userType: [
                UserType.External
            ],
            companyId: numericCompanyId,
            startRow: 0,
            endRow: 100
        });
        return [
            ...data.inProjectUsers,
            ...data.notInProjectUsers
        ];
    }, {
        enabled: !!numericCompanyId
    });
    const loadOptions = useCallback(async (input)=>{
        const data = await MembersAPI.fetchPaginatedMembersData({
            projectId: -1,
            userType: [
                UserType.External
            ],
            companyId: numericCompanyId,
            searchString: input,
            startRow: 0,
            endRow: 100
        });
        return [
            ...data.inProjectUsers,
            ...data.notInProjectUsers
        ];
    }, [
        numericCompanyId
    ]);
    return {
        defaultOptions: defaultUsers !== null && defaultUsers !== void 0 ? defaultUsers : [],
        loadOptions
    };
};
export const useNativeUserOptions = ()=>{
    const { data: defaultOptions = [] } = useQuery([
        'native-user-default-options'
    ], async ()=>{
        const data = await MembersAPI.fetchPaginatedMembersData({
            projectId: -1,
            userStatus: [
                UserStatus.Active,
                UserStatus.Invited
            ],
            startRow: 0,
            endRow: 50
        });
        return [
            ...data.inProjectUsers,
            ...data.notInProjectUsers
        ].filter((u)=>u.userType === UserType.Native).map(userOption);
    });
    const loadOptions = useCallback(async (inputValue)=>{
        const data = await MembersAPI.fetchPaginatedMembersData({
            searchString: inputValue,
            projectId: -1,
            userStatus: [
                UserStatus.Active,
                UserStatus.Invited
            ],
            startRow: 0,
            endRow: 50
        });
        return [
            ...data.inProjectUsers,
            ...data.notInProjectUsers
        ].filter((u)=>u.userType === UserType.Native).map(userOption);
    }, []);
    return {
        defaultOptions,
        loadOptions
    };
};
export const useActiveOrInvitedNonCustomerUsersQuery = ()=>{
    // console.log(
    //   '🟡 useActiveOrInvitedNonCustomerUsersQuery called from:',
    //   new Error().stack?.split('\n')[2],
    // );
    return useQuery({
        ...BaseMembersQueryOptions,
        select: (res)=>{
            return res.members.map(({ user })=>user).filter(({ userType, userStatus })=>{
                return userType !== UserType.External && userStatus !== UserStatus.Inactive;
            });
        }
    });
};
export const useCustomerUsersQuery = ()=>{
    // console.log(
    //   '🟠 useCustomerUsersQuery called from:',
    //   new Error().stack?.split('\n')[2],
    // );
    return useQuery({
        ...BaseMembersQueryOptions,
        select: (res)=>{
            return res.members.map(({ user })=>user).filter(({ userType })=>{
                return userType === UserType.External;
            });
        }
    });
};
const patchUsersByPayload = (previous, payload)=>{
    var _payload_fields, _previous_members_index_user_fields;
    if (!payload) {
        return previous;
    }
    const index = previous.members.findIndex((x)=>x.user.userId === payload.userId);
    if (index === -1) {
        return previous;
    }
    const newMembers = {
        ...previous
    };
    newMembers.members[index].user = {
        ...previous.members[index].user,
        ...payload,
        fields: unionBy((_payload_fields = payload.fields) !== null && _payload_fields !== void 0 ? _payload_fields : [], (_previous_members_index_user_fields = previous.members[index].user.fields) !== null && _previous_members_index_user_fields !== void 0 ? _previous_members_index_user_fields : [], 'fieldId')
    };
    if (previous.users !== undefined && newMembers.users !== undefined) {
        var _payload_fields1, _previous_members_userIndex_user_fields;
        const userIndex = previous.users.findIndex((x)=>x.userId === payload.userId);
        newMembers.users[userIndex] = {
            ...previous.users[userIndex],
            ...payload,
            fields: unionBy((_payload_fields1 = payload.fields) !== null && _payload_fields1 !== void 0 ? _payload_fields1 : [], (_previous_members_userIndex_user_fields = previous.members[userIndex].user.fields) !== null && _previous_members_userIndex_user_fields !== void 0 ? _previous_members_userIndex_user_fields : [], 'fieldId')
        };
    }
    return newMembers;
};
const hanldeMutate = async (queryClient)=>{
    const previous = queryClient.getQueryData(ALL_MEMBERS_QUERY_KEY);
    return {
        fallback: previous
    };
};
const handleOnError = (err, _payload, context, queryClient, translate)=>{
    var _err_request;
    const { fallback } = context || {};
    if (fallback) {
        queryClient.setQueryData(ALL_MEMBERS_QUERY_KEY, fallback);
    }
    if (err instanceof Error && (err === null || err === void 0 ? void 0 : (_err_request = err.request) === null || _err_request === void 0 ? void 0 : _err_request.status) === 403) {
        const errorMsg = translate('settings.team_members.edit.error');
        toastService({
            message: errorMsg
        }).error();
    }
};
export const useUpdateUserQuery = ()=>{
    const queryClient = useQueryClient();
    const translate = useTranslate();
    return useMutation((payload)=>UserAPI.save(payload), {
        onMutate: async (payload)=>{
            const previous = queryClient.getQueryData(ALL_MEMBERS_QUERY_KEY);
            if (previous) {
                const newMembers = patchUsersByPayload(previous, payload);
                queryClient.setQueryData(ALL_MEMBERS_QUERY_KEY, newMembers);
            }
            return {
                fallback: previous
            };
        },
        onError: (err, _payload, context)=>handleOnError(err, _payload, context, queryClient, translate),
        onSuccess: (data)=>{
            const previous = queryClient.getQueryData(ALL_MEMBERS_QUERY_KEY);
            if (previous) {
                const newMembers = patchUsersByPayload(previous, data);
                queryClient.setQueryData(ALL_MEMBERS_QUERY_KEY, newMembers);
            }
            queryClient.invalidateQueries(getPeopleHierarchyQueryKey(data.company.companyId));
            queryClient.invalidateQueries(getPeopleListQueryKey(data.company.companyId));
            invalidateSwrUser(data.userId);
        }
    });
};
const USER_LOOKUP_KEY = [
    'USER_TIMEOFF_LOOKUP'
];
export const useUserTimeoffLookup = ()=>{
    const { data: nativeActiveUsers = [] } = useNativeActiveOrInvitedUsersQuery();
    return useQuery(USER_LOOKUP_KEY, ()=>{
        return keyBy(nativeActiveUsers, 'userId');
    });
};
export const useUserLookupForProject = (projectId)=>{
    const { data: projectMembers } = useProjectMembersQuery(ProjectId(projectId));
    const query = useQuery([
        USER_LOOKUP_KEY,
        'project',
        projectId
    ], ()=>{
        var _ref;
        return keyBy((_ref = projectMembers === null || projectMembers === void 0 ? void 0 : projectMembers.userList) !== null && _ref !== void 0 ? _ref : [], 'userId');
    });
    return query;
};
// used inside timeline view for now
export const useUserTimeoffLookupForProject = (projectId)=>{
    const { data: projectMembers } = useProjectMembersQuery(ProjectId(projectId));
    return useQuery([
        USER_LOOKUP_KEY,
        'timeoff',
        'project',
        projectId
    ], ()=>{
        var _ref;
        var _projectMembers_members;
        // Filter for native active or invited users from project members
        const nativeActiveUsers = (_ref = projectMembers === null || projectMembers === void 0 ? void 0 : (_projectMembers_members = projectMembers.members) === null || _projectMembers_members === void 0 ? void 0 : _projectMembers_members.map(({ user })=>user).filter(({ userStatus, userType })=>(userStatus === UserStatus.Active || userStatus === UserStatus.Invited) && userType === UserType.Native)) !== null && _ref !== void 0 ? _ref : [];
        return keyBy(nativeActiveUsers, 'userId');
    });
};
export const useSmartUserLookup = (projectId)=>{
    const query = useQuery([
        USER_LOOKUP_KEY,
        'smart',
        projectId
    ], async ()=>{
        var _ref;
        var _data_members;
        if (projectId) {
            var _ref1;
            const data = await ProjectAPI.getMembers(projectId);
            return keyBy((_ref1 = data === null || data === void 0 ? void 0 : data.userList) !== null && _ref1 !== void 0 ? _ref1 : [], 'userId');
        }
        const data = await MembersAPI.findAll();
        return keyBy((_ref = data === null || data === void 0 ? void 0 : (_data_members = data.members) === null || _data_members === void 0 ? void 0 : _data_members.map(({ user })=>user)) !== null && _ref !== void 0 ? _ref : [], 'userId');
    });
    return query;
};
const patchMembersByPayload = (previous, payload)=>{
    var _payload_user_fields, _existingMember_user_fields;
    if (!payload) {
        return previous;
    }
    const index = previous.members.findIndex((x)=>x.user.userId === payload.user.userId);
    if (index === -1) {
        return previous;
    }
    const newMembers = JSON.parse(JSON.stringify(previous));
    const existingMember = newMembers.members[index];
    const updatedMember = {
        ...existingMember,
        ...payload,
        user: {
            ...existingMember.user,
            ...payload.user,
            fields: unionBy((_payload_user_fields = payload.user.fields) !== null && _payload_user_fields !== void 0 ? _payload_user_fields : [], (_existingMember_user_fields = existingMember.user.fields) !== null && _existingMember_user_fields !== void 0 ? _existingMember_user_fields : [], 'fieldId')
        }
    };
    newMembers.members[index] = updatedMember;
    return newMembers;
};
export const useTeamMemberGridToUpdate = ()=>{
    const qc = useQueryClient();
    const translate = useTranslate();
    return useMutation((payload)=>MembersAPI.updateMember(payload), {
        onMutate: async (payload)=>{
            const previous = qc.getQueryData(ALL_MEMBERS_QUERY_KEY);
            if (previous) {
                const newMembers = patchMembersByPayload(previous, payload);
                qc.setQueryData(ALL_MEMBERS_QUERY_KEY, newMembers);
            }
            return {
                fallback: previous
            };
        },
        onError: (err, _payload, context)=>{
            const { fallback } = context || {};
            if (fallback) {
                qc.setQueryData(ALL_MEMBERS_QUERY_KEY, fallback);
            }
            if (err instanceof Error) {
                const errorMsg = translate('settings.team_members.edit.error');
                toastService({
                    message: errorMsg
                }).error();
            }
        },
        onSuccess: (_data, variables)=>{
            qc.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
            invalidateSwrUser(variables.user.userId);
        }
    });
};
export const getProjectMembersQueryKeyV2 = ([, projectId])=>[
        'squad',
        projectId
    ];
export const useProjectMembersInSettingsPageQuery = (projectId, options = {})=>useQuery(getProjectMembersQueryKey(projectId), ()=>ProjectAPI.getMembers(projectId[1]), {
        ...options
    });
export const useUpdateExternalMembersMutation = ()=>{
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload)=>MembersAPI.updateExternalMembers(payload),
        onSuccess: (_, variables)=>{
            const ProjectID = (id)=>[
                    'project_id',
                    id
                ];
            qc.invalidateQueries(getProjectMembersQueryKeyV2(ProjectID(variables.projectId)));
        }
    });
};
export const useBulkUpdateOrgChart = (companyId)=>{
    const queryClient = useQueryClient();
    const translate = useTranslate();
    return useMutation({
        mutationFn: (payload)=>UserAPI.bulkUpdateOrgChart(payload),
        onMutate: ()=>hanldeMutate(queryClient),
        onError: (err, _payload, context)=>handleOnError(err, _payload, context, queryClient, translate),
        onSuccess: (data)=>{
            const previous = queryClient.getQueryData(ALL_MEMBERS_QUERY_KEY);
            let finalMembers = {};
            if (previous !== undefined) {
                finalMembers = data.reduce((oldMembers, currentUser)=>patchUsersByPayload(oldMembers, currentUser), previous);
                queryClient.setQueryData(ALL_MEMBERS_QUERY_KEY, finalMembers);
            }
            if (isEmpty(finalMembers)) {
                queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
            }
            queryClient.invalidateQueries(getPeopleHierarchyQueryKey(companyId));
            queryClient.invalidateQueries(getPeopleListQueryKey(companyId));
        }
    });
};
export const useReplaceUserOrgChart = (companyId)=>{
    const queryClient = useQueryClient();
    const translate = useTranslate();
    return useMutation({
        mutationFn: ({ oldUser, newUser })=>UserAPI.replaceUserInOrgChart(oldUser, newUser),
        onMutate: ()=>hanldeMutate(queryClient),
        onError: (err, _payload, context)=>handleOnError(err, _payload, context, queryClient, translate),
        onSuccess: (data, variables)=>{
            const previous = queryClient.getQueryData(ALL_MEMBERS_QUERY_KEY);
            const { oldUser, newUser } = variables;
            let finalMembers = {};
            const newUserIndex = previous === null || previous === void 0 ? void 0 : previous.members.findIndex((member)=>member.user.userId === newUser);
            const oldUserIndex = previous === null || previous === void 0 ? void 0 : previous.members.findIndex((member)=>member.user.userId === oldUser);
            if (newUserIndex && oldUserIndex && previous) {
                const newUserData = previous.members[newUserIndex].user;
                const oldUserData = previous.members[oldUserIndex].user;
                newUserData.reportingTo = oldUserData.reportingTo;
                newUserData.managerOf = oldUserData.managerOf;
                oldUserData.reportingTo = undefined;
                oldUserData.managerOf = undefined;
                const newUserPatchedData = patchUsersByPayload(previous, newUserData);
                finalMembers = patchUsersByPayload(newUserPatchedData, oldUserData);
            }
            queryClient.setQueryData(ALL_MEMBERS_QUERY_KEY, finalMembers);
            queryClient.invalidateQueries(getPeopleHierarchyQueryKey(companyId));
            queryClient.invalidateQueries(getPeopleListQueryKey(companyId));
        }
    });
};
export const useExternalTeamMemberGridToUpdate = (projectId)=>{
    const queryClient = useQueryClient();
    return useMutation((payload)=>MembersAPI.updateMember(payload), {
        onSuccess: async (data)=>{
            var _data_company;
            // queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
            await queryClient.invalidateQueries(getProjectMembersQueryKey(ProjectId(projectId)));
            if (data === null || data === void 0 ? void 0 : (_data_company = data.company) === null || _data_company === void 0 ? void 0 : _data_company.companyId) {
                await queryClient.invalidateQueries(getPeopleListQueryKey(data.company.companyId));
            }
        }
    });
};
// Do not cache this query because it is used in a modal that is opened and closed frequently
// Also since the user search is a typeahead, we don't want to cache the results
// This query is used in the AssigneePicker and @mention components mostly
export const useFetchPaginatedMembersData = (params, options)=>{
    return useQuery([
        'paginated-members',
        params.projectId,
        params.assigneePickerFilter,
        params.isPrivate,
        params.includeRestricedPartnersMembers,
        params.searchString,
        params.userType,
        params.userStatus,
        params.taskStartDateTime,
        params.taskEndDateTime,
        params.fetchUnResolvedPlaceholdersOnly,
        params.groupId,
        params.includedUserIds,
        params.includedTeamIds,
        params.fieldFilters,
        params.startRow,
        params.endRow
    ], ()=>MembersAPI.fetchPaginatedMembersData(params), {
        cacheTime: 0,
        ...options
    });
};
export const MEMBER_INTEGRATIONS_QUERY_KEY = [
    'member-integrations'
];
export const useMemberIntegrationsQuery = (options)=>{
    return useQuery(MEMBER_INTEGRATIONS_QUERY_KEY, ()=>MembersAPI.getMemberIntegrations(), {
        ...options
    });
};
export const useUpdateMemberIntegrations = ()=>{
    const queryClient = useQueryClient();
    return useMutation((data)=>MembersAPI.updateMemberIntegrations(data), {
        onSuccess: ()=>{
            queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
            queryClient.invalidateQueries(MEMBER_INTEGRATIONS_QUERY_KEY);
        }
    });
};
